/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team <mailto:radmanesh@gmail.com Arman Radmanesh> <mailto:salarirad@gmail.com Salar Rad>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { doc, getDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { generateVerificationCode } from '../utils/verificationUtils';


/**
 *
 * @param {string} pId is the prolific id of the participant (in string) we need this to find the existing participant,
 * or create a new participant
 * @param {string} projectId is the project id of the project (in string) for which we are creating the participant
 * @returns {string} the id of the participant document in the Firestore or rejects with an error
 */
const findOrCreateNewParticipant = async (pId, projectId, registrationType='prolific') => {
  const findOrCreateNewParticipant = httpsCallable(functions, 'findOrCreateNewParticipant');
  try {
    console.debug("findOrCreateNewParticipant called with pId:", pId, "projectId:", projectId, "registrationType:", registrationType);
    const result = await findOrCreateNewParticipant({ pId, projectId, registrationType });
    console.log("findOrCreateNewParticipant result:", result);
    console.log("findOrCreateNewParticipant result:", JSON.stringify(result, null, 2));
    return result.data?.participantId;
  } catch (err) {
    console.error('Error creating participant:', err);
    return Promise.reject(err);
  }
};

/**
 * Search for a participant in Firestore by pId and projectId. If found, return the participantId.
 * @param {string} pId - The participant ID.
 * @param {string} projectId - The project ID.
 * @returns {Promise<string>} - The participantId if found, otherwise rejects with an error.
 */
async function findParticipantIdByPIdAndProjectId(pId, projectId) {
  // use findParticipantByProlificIdAndProjectId onCall function from the backend
  const findParticipantByPIdAndProjectId = httpsCallable(functions, 'findParticipantByProlificIdAndProjectId');
  try {
    const result = await findParticipantByPIdAndProjectId({ pId, projectId });
    // check for the result and if it is successful return the data
    // or handle the error
    if(result.data?.participantId)
      return result.data?.participantId;
    else
      return Promise.reject("Participant not found");
  } catch (err) {
    console.error('Error finding participant:', err);
    return Promise.reject(err);
  }
}

/**
 * This function finds a participant by the participantId from the Firestore or returns null if not found
 * @param {String} participantId The id of the participant document in the Firestore
 * @returns {Promise<Object|null>} partipant object if found, otherwise rejects with an error
 */
const findParticipantById = async (participantId) => {
  // use findParticipantByParticipantId onCall function from the backend
  const findParticipantById = httpsCallable(functions, 'findParticipantByParticipantId');
  try {
    const result = await findParticipantById({ participantId });
    console.log("result", result);
    // check for the result and if it is successful return the data
    // or handle the error
    if(result.data?.participant)
      return result.data?.participant;
    else
      return Promise.reject("Participant not found");
  } catch (err) {
    console.error('Error finding participant:', err);
    return Promise.reject(err);
  }
};

/**
 * This function updates the registration step of the participant in the Firestore using the backend
 * @param {string} participantId
 * @param {Integer} registrationStep
 * @param {Object} additionalData
 * @returns participant object if updated successfully, otherwise rejects with an error
*/
const updateParticipantRegistration = async (participantId, registrationStep = -1, additionalData = {}) => {
  // use updateParticipantRegistration onCall function from the backend
  const updateParticipantRegistration = httpsCallable(functions, 'updateParticipantRegistration');
  try {
    const result = await updateParticipantRegistration({ participantId, registrationStep, additionalData });
    // check for the result and if it is successful return the data
    // or handle the error
    if(result.data?.participant)
      return result.data?.participant;
    else
      return Promise.reject("Participant not found");
  }
  catch (err) {
    console.error('Error updating participant:', err);
    return Promise.reject(err);
  }
}

/**
 * Checks if a project is available for registration by validating if current date
 * falls within the project's invitation window.
 *
 * @param {string} projectId - The ID of the project to check.
 * @returns {Promise<boolean>} - A promise that resolves to true if the project is available for registration, otherwise false.
 */
const checkProjectForRegistration = async (projectId) => {
  if (!projectId) return false;
  // Asked on the server: project documents are admin-only in Firestore rules.
  const check = httpsCallable(functions, 'checkProjectForRegistration');
  const result = await check({ projectId });
  return result?.data?.open === true;
}

/**
 * Finds a project for registration by its ID.
 *
 * @param {string} projectId - The ID of the project to find.
 * @returns {Promise<Object|null>} The project object if found, otherwise null.
 * @throws Will throw an error if the projectId is not provided or is an empty string.
 */
const findProjectForRegistration = async (projectId) => {
  if (!projectId || projectId === "") {
    console.error("projectId not Passed");
    return null;
  }
  //get project from firestore
  const projectRef = doc(db, "projects", projectId);
  const projectSnap = await getDoc(projectRef);
  if (!projectSnap.exists()) {
    console.error("Project not found");
    return null;
  } else {
    return projectSnap.data();
  }
}

export const inviteParticipantToProject = async (email, projectId ='YIqY2iukrk45BvgSJ2AN') => {
  try {
    // Create registration data according to schema
    const registrationData = {
      projectId: projectId, // Project identifier
      registrationType: 'invitation', // Type of registration
      registration: {
        email: {
          address: email, // User's email address
          verified: false, // Initial verification status
          verificationCode: generateVerificationCode(), // Generated verification code
          verificationSentAt: serverTimestamp() // When code was sent
        }
      },
      registrationStep: 1, // Initial registration step
      registrationStartedAt: serverTimestamp(), // When registration began
      registrationStepUpdatedAt: serverTimestamp(), // When step was last updated
      completed: false, // Registration completion status
      createdAt: serverTimestamp(), // Creation timestamp
      updatedAt: serverTimestamp(), // Update timestamp
      // Initialize empty objects for optional fields
      profile: {},
      fitbitData: {}, // Initialize empty object for optional field
      fitbitRequest: {}, // Initialize empty object for optional field
    };

    console.log("Verification code:", registrationData.registration.email.verificationCode);

    // Add to Firestore
    const docRef = await addDoc(collection(db, "participants"), registrationData);
    console.log("Participant invited with ID:", docRef.id);
    return docRef.id; // Return the document ID of the newly created participant
  }
  catch (error) {
    console.error("Error inviting participant:", error);
    throw error; // Propagate the error for further handling
  }
}

export {
  checkProjectForRegistration, findOrCreateNewParticipant,
  findParticipantById, findParticipantIdByPIdAndProjectId, findProjectForRegistration, updateParticipantRegistration
};
